import { ITaxStatus } from '../../../store/user/facade/user.facade';
import { TaxRegistrationStatus } from './../../../models/entity';
import { IAppState } from './../../state/app.state';
import { TFASteps } from '../../../models/auth';
import { AccountVM } from '../../../models/account';
import * as SystemActions from '../../../store/system/actions/system.actions';
import * as UserActions from '../../../store/user/actions/user.actions';
import * as AuthActions from '../../../store/auth/actions/auth.actions';
import { SMSVerificationStatus } from 'src/app/models/smsVerificationStatus';
import { IUserAddress } from '../../../models/userAddress';

export interface ViewProfileState {
    userProfile: AccountVM;
    taxStatus: ITaxStatus;
    userAddress: IUserAddress;
    profileTFATypeUpdatedSuccess: boolean;
    requestSMSVerificationSuccess: boolean;
    SMSVerificationStatus: SMSVerificationStatus;
    profileUpdatedSuccess: boolean;
    profileUpdatedFail: boolean;
    successText: string;
    failText: string;
    loading: boolean;
    editMode: boolean;
    TFAStep: number;
    requireUserToSetTwoFactorAuthentication: boolean;
}

export const initialViewProfileState: ViewProfileState = {
    userProfile: null,
    taxStatus: null,
    userAddress: null,
    profileTFATypeUpdatedSuccess: false,
    requestSMSVerificationSuccess: false,
    SMSVerificationStatus: SMSVerificationStatus.undefined,
    profileUpdatedSuccess: false,
    successText: '',
    profileUpdatedFail: false,
    failText: '',
    loading: false,
    editMode: false,
    TFAStep: TFASteps.START_SET_TYPE,
    requireUserToSetTwoFactorAuthentication: null
};

export function ViewProfileReducer(state = initialViewProfileState, action: UserActions.UserActions | AuthActions.AuthActions | SystemActions.SystemActions) {
    switch (action.type) {
        case UserActions.USER_PROFILE_EDIT:
            return {
                ...state,
                editMode: true
            };
        case UserActions.USER_PROFILE_EDIT_CLOSE:
            return {
                ...state,
                editMode: false
            };
        case UserActions.UPDATE_USER_PROFILE:
            return {
                ...state,
                loading: true,
                profileUpdatedSuccess: false,
                profileUpdatedFail: false
            };
        case AuthActions.AUTHENTICATE:
            return {
                ...state,
                userAddress: null,
                userProfile: {
                    ...state.userProfile,
                    email: action.payload.username
                },
            };
        case UserActions.UPDATE_USER_PROFILE_FAIL:
            return {
                ...state,
                loading: false,
                profileUpdatedSuccess: false,
                profileUpdatedFail: true,
                failText: action.payload,
                successText: ''
            };
        case UserActions.UPDATE_USER_PROFILE_SUCCESS:
            return {
                ...state,
                loading: false,
                profileUpdatedSuccess: true,
                successText: action.payload.message,
                userProfile: {
                    ...state.userProfile,
                    ...action.payload.userProfile
                },
                profileUpdatedFail: false
            };
        case UserActions.SET_USER_PROFILE:
            return {
                ...state,
                userProfile: {
                    ...action.payload
                },
                loading: false,
                profileUpdatedFail: false
            };
        case UserActions.RESET_USER_PROFILE_FORM:
            return {
                ...state,
                loading: false,
                profileUpdatedFail: false,
                profileUpdatedSuccess: false
            };
        case UserActions.SAVE_USER_PROFILE_TFA_TYPE_EMAIL:
            return {
                ...state,
                userProfile: {
                    ...state.userProfile,
                    twoFactorType: action.payload.twoFactorType
                },
                loading: true,
            };
        case UserActions.SAVE_USER_PROFILE_TFA_TYPE_EMAIL_SUCCESS:
            return {
                ...state,
                loading: false,
                TFAStep: TFASteps.EMAIL_CONFIRM,
                userProfile: {
                    ...state.userProfile,
                    twoFactorEnabled: true
                },
            };
        case UserActions.SAVE_USER_PROFILE_TFA_TYPE_EMAIL_FAIL:
            return {
                ...state,
                loading: false,
                TFAStep: TFASteps.START_SET_TYPE
            };
        case UserActions.UPDATE_USER_PROFILE_TFA_TYPE_SMS:
            return {
                ...state,
                userProfile: {
                    ...state.userProfile,
                    twoFactorType: action.payload
                },
                TFAStep: TFASteps.SMS_REQUEST
            };
        case UserActions.REQUEST_SMS_VERIFICATION:
            return {
                ...state,
                loading: true,
                requestSMSVerificationSuccess: false
            };
        case UserActions.REQUEST_SMS_VERIFICATION_SUCCESS:
            return {
                ...state,
                loading: false,
                requestSMSVerificationSuccess: true,
                TFAStep: TFASteps.SMS_VERIFY
            };
        case UserActions.REQUEST_SMS_VERIFICATION_FAIL:
            return {
                ...state,
                loading: false,
                requestSMSVerificationSuccess: false,
                failText: action.payload
            };
        case UserActions.VERIFY_SMS:
            return {
                ...state,
                loading: true,
                SMSVerificationStatus: SMSVerificationStatus.undefined
            };
        case UserActions.VERIFY_SMS_SUCCESS:
            return {
                ...state,
                loading: false,
                SMSVerificationStatus: SMSVerificationStatus.success,
                TFAStep: TFASteps.SMS_CONFIRM,
                userProfile: {
                    ...state.userProfile,
                    isMobileVerified: true
                }
            };
        case UserActions.VERIFY_SMS_FAIL:
            return {
                ...state,
                loading: false,
                SMSVerificationStatus: SMSVerificationStatus.failure,
                failText: action.payload,
                userProfile: {
                    ...state.userProfile,
                    isMobileVerified: false
                }
            };
        case UserActions.SAVE_USER_PROFILE_TFA_TYPE_SMS:
            return {
                ...state,
                loading: true,
                TFAStep: TFASteps.SMS_VERIFY
            };
        case UserActions.SAVE_USER_PROFILE_TFA_TYPE_SMS_SUCCESS:
            return {
                ...state,
                loading: false,
                profileTFATypeUpdatedSuccess: true,
                TFAStep: TFASteps.SMS_CONFIRM,
                userProfile: {
                    ...state.userProfile,
                    twoFactorEnabled: true
                }
            };
        case UserActions.SAVE_USER_PROFILE_TFA_TYPE_SMS_FAIL:
            return {
                ...state,
                loading: false,
                profileTFATypeUpdatedSuccess: false
            };
        case UserActions.RESET_PROFILE_TFA:
            return {
                ...state,
                profileTFATypeUpdatedSuccess: false,
                requestSMSVerificationSuccess: false,
                SMSVerificationStatus: SMSVerificationStatus.undefined
            };
        case UserActions.TFA_STEP:
            return {
                ...state,
                TFAStep: action.payload
            };
        case UserActions.UPDATE_TFA:
            return {
                ...state,
                userProfile: {
                    ...state.userProfile,
                    twoFactorEnabled: action.payload
                }
            };
        case AuthActions.CHANGE_PASSWORD_SUCCESS:
            return {
                ...state,
                loading: false,
                userProfile: {
                    ...state.userProfile,
                    doPasswordChange: false
                }
            };
        case SystemActions.CLEAR_SYSTEM:
            return {
                ...initialViewProfileState
            };
        case UserActions.SET_TAX_STATUS:
            return {
                ...state,
                loading: true
            };
        case UserActions.GET_TAX_STATUS_SUCCESS:
        case UserActions.SET_TAX_STATUS_SUCCESS: {
            return {
                ...state,
                loading: false,
                taxStatus: {
                    taxNumber: action.payload.taxNumber,
                    taxRegistrationStatus: action.payload.taxRegistrationStatus
                }
            };
        };
        case UserActions.GET_USER_REQUIRED_TO_SET_TWO_FACTOR_AUTHENTICATION_SUCCESS:
            return {
                ...state,
                loading: false,
                requireUserToSetTwoFactorAuthentication: action.payload
            };
        case UserActions.RESET_SMS_VERIFICATION_STATUS: 
            return {
                ...state,
                SMSVerificationStatus: SMSVerificationStatus.undefined
            };
        case UserActions.GET_ADDRESS:
            return {
                ...state,
                loading: true
            };
        case UserActions.GET_ADDRESS_SUCCESS: {
            return {
                ...state,
                loading: false,
                userAddress: action.payload
            };
        };
        case UserActions.SET_ADDRESS_SUCCESS: {
            return {
                ...state,
                loading: false,
                profileUpdatedSuccess: true,
                userAddress: action.payload
            };
        };
        default:
            return state;
    }
}